import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';

const getApiErrorMessage = (error: any, fallback: string) => {
  const data = error.response?.data;
  if (data?.field_errors) {
    return Object.entries(data.field_errors)
      .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
      .join(' | ');
  }
  return data?.error || data?.detail || fallback;
};

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  phone?: string;
  profile_image?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean }>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login(email, password);

          const { access, refresh } = response.data;
          
          localStorage.setItem('accessToken', access);
          localStorage.setItem('refreshToken', refresh);
          
          await get().fetchProfile();
          set({ isAuthenticated: true, isLoading: false });
          return { mfaRequired: false };
        } catch (error: any) {
          set({
            error: getApiErrorMessage(error, 'Login failed. Please check your email and password.'),
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (data: any) => {
        set({ isLoading: true, error: null });
        try {
          await authAPI.register(data);
          await get().login(data.email, data.password);
        } catch (error: any) {
          set({
            error: getApiErrorMessage(error, 'Registration failed. Please check your details.'),
            isLoading: false,
          });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        set({ user: null, isAuthenticated: false });
      },

      fetchProfile: async () => {
        try {
          const response = await authAPI.getProfile();
          set({ user: response.data, isAuthenticated: true });
        } catch (error) {
          set({ user: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
);
